"use client";

import { useEffect, useRef, useState, useId } from "react";
import Icon from "@/components/icons";
import { cn } from "@/lib/utils";
import { Accordion } from "@/types/Accordion";
import { ElementType } from "@/constants/tagular/main";
import { ElementClicked } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementClicked";
import useElementEvents from "@/hooks/eventing/useElementEvents";

type Styles = {
  [key: string]: {
    icon: string;
    content: string;
  };
};

export type StickyNavMobileProps = {
  accordions?: Accordion[];
  defaultOpenedAccordion?: string | null;
  handleAccordionClick?: (accordion: string | null) => void;
};

export const StickyNavMobile = ({
  accordions: navItems = [],
  defaultOpenedAccordion,
  handleAccordionClick,
}: StickyNavMobileProps) => {
  const listRef = useRef<HTMLUListElement | null>(null);
  const baseId = useId();

  const [isAtTop, setIsAtTop] = useState(true);
  const [selectedAccordion, setSelectedAccordion] = useState<string | null>(
    null,
  );

  const accordionStyles: Styles = {
    true: {
      icon: "rotate-90",
      content: "h-auto px-4 py-8 block",
    },
    false: {
      icon: "-rotate-90",
      content: "h-0 overflow-hidden hidden",
    },
  };

  const baseWebElement: ElementClicked["webElement"] = {
    elementType: ElementType.Link,
    location: "degree-hub-toc-mobile",
  };

  const { elementClicked: triggerElementClick } = useElementEvents({
    webElement: baseWebElement,
  });

  const handleElementClick = (item: Accordion, index: number) => {
    const itemWebElement: ElementClicked["webElement"] = {
      elementType: ElementType.Link,
      htmlId: item.htmlId,
      location: "degree-hub-toc-mobile",
      position: `${index + 1}`,
      text: item.text,
    };

    triggerElementClick({ webElement: itemWebElement });
  };

  useEffect(() => {
    if (!defaultOpenedAccordion) return;

    setSelectedAccordion(defaultOpenedAccordion);
  }, [defaultOpenedAccordion]);

  useEffect(() => {
    const onScroll = () => {
      if (listRef.current) {
        const isTop = window.scrollY - listRef.current.offsetTop < 0;
        setIsAtTop(isTop);
      }
    };

    onScroll();

    window.addEventListener("scroll", onScroll);

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav className="-mx-4" aria-label="Page sections">
      <ul ref={listRef} className="flex flex-col w-full list-none !px-0 m-0">
        {navItems?.map((item, index) => {
          const optionSelected =
            item.text === selectedAccordion ? null : item.text;

          const isExpanded = item.text === selectedAccordion;
          const key = String(isExpanded);
          const panelId = `${baseId}-panel-${index}`;
          const headingId = `${baseId}-heading-${index}`;

          return (
            <li key={item.text} className={"p-0"}>
              <h3 className="m-0 p-0" id={headingId}>
                <button
                  onClick={() => {
                    handleAccordionClick?.(optionSelected);
                    setSelectedAccordion(optionSelected);
                    handleElementClick(item, index);
                  }}
                  className={cn(
                    isAtTop ? "z-10" : "z-30",
                    selectedAccordion ? "sticky top-0" : "",
                    "flex items-center justify-between p-6 px-4 w-full",
                    "text-white bg-secondary-500 border-b border-putty-100",
                    "font-normal text-base text-left cursor-pointer",
                  )}
                  aria-expanded={isExpanded}
                  aria-controls={panelId}
                  id={item?.anchor}
                >
                  <span>{item.text}</span>
                  <span
                    className={accordionStyles[key].icon}
                    aria-hidden="true"
                  >
                    <Icon
                      type="chevron-left"
                      svgProps={{ width: 24, height: 24, "aria-hidden": true }}
                    />
                  </span>
                </button>
              </h3>
              <div
                id={panelId}
                role="region"
                aria-labelledby={headingId}
                className={cn(accordionStyles[key].content, item?.styles ?? "")}
                hidden={!isExpanded}
              >
                {item?.content}
              </div>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
